<template>
  <div class="segment-bar-input">
    <div
      ref="barEl"
      class="segment-bar"
      :class="{ 'is-locked': locked }"
      role="slider"
      tabindex="0"
      :aria-label="label"
      :aria-valuemin="0"
      :aria-valuemax="values.length - 1"
      :aria-valuenow="selectedIndex"
      :aria-valuetext="zoneLabels[zoneByIndex[selectedIndex]]"
      :aria-disabled="locked"
      :data-testid="testId"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="stopDrag"
      @pointercancel="stopDrag"
      @keydown="onKeydown">
      <!-- One grid cell per segment: zone separators are borders, fill and selection backgrounds.
           Borders snap to whole device pixels, so every separator looks the same at any zoom. -->
      <div
        v-for="(_, index) in values"
        :key="`tile-${index}`"
        v-tooltip="{ text: locked ? lockedTooltip : '', portal: true, theme: tooltipTheme }"
        class="segment-tile"
        :class="{
          'is-zone-start': ZONE_STARTS.has(index),
          'is-last': index === values.length - 1,
          'is-filled': index < selectedIndex,
          'is-active': index === selectedIndex,
        }"
        :data-segment-index="index">
        <span class="segment-fill"></span>
      </div>
    </div>
    <div v-if="showZoneLabels" class="segment-zone-labels" aria-hidden="true">
      <span v-for="zone in COMPLEXITY_SEGMENT_ZONES" :key="zone" :style="{ gridColumn: zoneColumns[zone] }">
        {{ zoneLabels[zone] }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { hideTooltip } from '@/helpers/tooltip'
import {
  COMPLEXITY_SEGMENT_ZONES,
  COMPLEXITY_SEGMENT_ZONE_BY_INDEX,
  nearestComplexitySegmentIndex,
  type ComplexitySegmentZone,
} from '@arcexperts/core-types'

/** First segment of each zone: it carries the zone separator; the others start with a white gap. */
const ZONE_STARTS = new Set(
  COMPLEXITY_SEGMENT_ZONE_BY_INDEX.flatMap((zone, index) =>
    index === 0 || COMPLEXITY_SEGMENT_ZONE_BY_INDEX[index - 1] !== zone ? [index] : []
  )
)

/** Grid columns of each zone label, so the labels line up with their segments. */
const zoneColumns = Object.fromEntries(
  COMPLEXITY_SEGMENT_ZONES.map((zone) => {
    const first = COMPLEXITY_SEGMENT_ZONE_BY_INDEX.indexOf(zone)
    const last = COMPLEXITY_SEGMENT_ZONE_BY_INDEX.lastIndexOf(zone)
    return [zone, `${first + 1} / ${last + 2}`]
  })
) as Record<ComplexitySegmentZone, string>

const props = withDefaults(
  defineProps<{
    modelValue: number
    /** The 11 segment values, ascending. */
    values: readonly number[]
    zoneLabels: Record<ComplexitySegmentZone, string>
    label: string
    /** Without a justification: selecting asks for one instead (`locked-click` with the requested value). */
    locked?: boolean
    lockedTooltip?: string
    testId?: string
    /** A group of bars shows the range labels below its last bar only. */
    showZoneLabels?: boolean
  }>(),
  { showZoneLabels: true }
)

const emit = defineEmits<{
  'update:modelValue': [value: number]
  /** The value the user asked for, or undefined when no particular segment was chosen. */
  'locked-click': [requested: number | undefined]
}>()

const tooltipTheme = { placement: 'top', width: '14rem', padding: '0.6em 1em', 'text-align': 'center' } as const
const zoneByIndex = COMPLEXITY_SEGMENT_ZONE_BY_INDEX
const barEl = ref<HTMLElement | null>(null)
const selectedIndex = computed(() => nearestComplexitySegmentIndex(props.values, props.modelValue ?? 1))

function select(index: number) {
  const clamped = clampedIndex(index)
  if (clamped === selectedIndex.value && props.values[clamped] === props.modelValue) return
  emit('update:modelValue', props.values[clamped])
}

function indexAt(clientX: number): number {
  const rect = barEl.value?.getBoundingClientRect()
  if (!rect || rect.width === 0) return selectedIndex.value
  return Math.floor(((clientX - rect.left) / rect.width) * props.values.length)
}

let dragging = false

function clampedIndex(index: number): number {
  return Math.max(0, Math.min(props.values.length - 1, index))
}

/** Asks for the justification; the hovered segment's tooltip would otherwise stay open above the dialog. */
function requestUnlock(index?: number, hovered?: EventTarget | null) {
  hideTooltip(hovered ?? null)
  emit('locked-click', index === undefined ? undefined : props.values[clampedIndex(index)])
}

function onPointerDown(event: PointerEvent) {
  if (props.locked) {
    requestUnlock(indexAt(event.clientX), event.target)
    return
  }
  dragging = true
  barEl.value?.setPointerCapture?.(event.pointerId)
  select(indexAt(event.clientX))
}

function onPointerMove(event: PointerEvent) {
  if (dragging) select(indexAt(event.clientX))
}

function stopDrag() {
  dragging = false
}

function onKeydown(event: KeyboardEvent) {
  if (props.locked && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault()
    requestUnlock()
    return
  }
  const next: Record<string, number> = {
    ArrowLeft: selectedIndex.value - 1,
    ArrowDown: selectedIndex.value - 1,
    ArrowRight: selectedIndex.value + 1,
    ArrowUp: selectedIndex.value + 1,
    Home: 0,
    End: props.values.length - 1,
  }
  if (!(event.key in next)) return
  event.preventDefault()
  if (props.locked) requestUnlock(next[event.key])
  else select(next[event.key])
}
</script>

<style scoped>
.segment-bar-input {
  @apply min-w-0;
}

.segment-bar {
  /* z-index 0 keeps the global hover raise of the .data-v-tooltip segments inside the bar; the
     tooltips are portals, and the raise would paint the bar above the justification dialog. */
  position: relative;
  z-index: 0;
  display: grid;
  grid-template-columns: repeat(11, minmax(0, 1fr));
  height: 28px;
  cursor: pointer;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.segment-bar:focus-visible {
  outline: 2px solid var(--color-primary-default);
  outline-offset: 2px;
}

.segment-tile {
  position: relative;
  min-width: 0;
}

.segment-tile.is-zone-start {
  border-left: 1px solid #8a919c;
}

.segment-tile.is-last {
  border-right: 1px solid #8a919c;
}

.segment-fill {
  position: absolute;
  top: 7px;
  right: 0;
  left: 0;
  height: 14px;
  background: #eef0f4;
  pointer-events: none;
  transition: background-color 0.1s ease-out;
}

/* White gap between two segments of the same zone */
.segment-tile:not(.is-zone-start) .segment-fill {
  left: 2px;
}

.segment-tile.is-filled .segment-fill {
  background: var(--color-primary-soft);
}

.segment-tile.is-active .segment-fill {
  background: var(--color-primary-default);
}

.segment-zone-labels {
  display: grid;
  grid-template-columns: repeat(11, minmax(0, 1fr));
  margin-top: 2px;
}

.segment-zone-labels span {
  min-width: 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.3;
  @apply text-neutral-550;
}
</style>
